import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { Building, Plus, Pencil, Trash2, Loader2, AlertCircle } from 'lucide-react';
import { MerchantForm } from './MerchantForm';
import { MerchantFeeForm } from './MerchantFeeForm';
import { Link } from 'react-router-dom';
import { DeleteConfirmationModal } from '../common/DeleteConfirmationModal';
import { Merchant } from '../../types';

export function MerchantManagement() {
  const [merchants, setMerchants] = useState<Merchant[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNewMerchantForm, setShowNewMerchantForm] = useState(false);
  const [selectedMerchant, setSelectedMerchant] = useState<Merchant | null>(null);
  const [merchantToDelete, setMerchantToDelete] = useState<Merchant | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    fetchMerchants();
  }, []);

  async function fetchMerchants() {
    try {
      const { data, error } = await supabase
        .from('merchants')
        .select('*')
        .order('name');
      
      if (error) throw error;
      setMerchants(data || []);
    } catch (error) {
      console.error('Error fetching merchants:', error);
    } finally {
      setLoading(false);
    }
  }

  const handleDelete = async () => {
    if (!merchantToDelete) return;
    
    setDeleting(true);
    setDeleteError(null);

    try {
      const { error } = await supabase
        .rpc('soft_delete_merchant', {
          merchant_id_param: merchantToDelete.id
        });

      if (error) throw error;
      
      setMerchants(merchants.filter(m => m.id !== merchantToDelete.id));
      setMerchantToDelete(null);
    } catch (err) {
      console.error('Error deleting merchant:', err);
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete merchant');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      {deleteError && (
        <div className="flex items-center gap-2 p-3 rounded-xl bg-red-50 border border-red-100">
          <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
          <p className="text-[13px] text-red-600">{deleteError}</p>
        </div>
      )}

      <div className="page-header">
        <div>
          <h1 className="page-title">Merchants</h1>
          <p className="page-subtitle">Manage merchants and their transaction fee structures</p>
        </div>
        <button onClick={() => setShowNewMerchantForm(true)} className="btn-primary">
          <Plus className="w-4 h-4" />
          Add Merchant
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      ) : merchants.length === 0 ? (
        <div className="text-center py-16">
          <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
            <Building className="w-7 h-7 text-slate-400" />
          </div>
          <h3 className="text-[15px] font-semibold text-slate-900">No merchants</h3>
          <p className="mt-1 text-[14px] text-slate-500">Get started by adding a new merchant.</p>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="px-5 py-3 text-left text-[12px] font-medium text-slate-400 uppercase tracking-wider">Name</th>
                  <th className="px-5 py-3 text-right text-[12px] font-medium text-slate-400 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {merchants.map((merchant) => (
                  <tr key={merchant.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-5 py-4">
                      <Link
                        to={`/merchants/${merchant.id}`}
                        className="text-[14px] font-medium text-slate-900 hover:text-violet-600 transition-colors"
                      >
                        {merchant.name}
                      </Link>
                    </td>
                    <td className="px-5 py-4 text-right">
                      <div className="flex items-center justify-end gap-0.5">
                        <button
                          onClick={() => setSelectedMerchant(merchant)}
                          className="btn-ghost"
                          title="Edit fees"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setMerchantToDelete(merchant)}
                          disabled={deleting}
                          className="btn-ghost hover:!text-red-500 hover:!bg-red-50"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {selectedMerchant && (
        <MerchantFeeForm
          merchant={selectedMerchant}
          onClose={() => setSelectedMerchant(null)}
        />
      )}
      {showNewMerchantForm && (
        <MerchantForm
          onClose={() => setShowNewMerchantForm(false)}
          onSuccess={() => {
            fetchMerchants();
          }}
        />
      )}
      <DeleteConfirmationModal
        title="Delete Merchant"
        message={`Are you sure you want to delete "${merchantToDelete?.name}"? This merchant will no longer be available for new quotations, but existing quotations will be preserved.`}
        isOpen={!!merchantToDelete}
        isDeleting={deleting}
        onConfirm={handleDelete}
        onCancel={() => setMerchantToDelete(null)}
      />
    </div>
  );
}
